// src/components/calculator/IrpfResultPanels.js
//
// Ticket 069 (D5). The IRPF result panels, MOVED unchanged from
// src/pages/CalculatorResult.js so the Calculator result page and the Advisor
// Intake results page draw the same estimate from one place. They depend on each
// other (the hero text points at the adjustment cards, table rows are starred for
// them), so they move together. No logic change.
//
// IrpfEstimateBody renders everything from the hero through the disclaimer for a
// non-blocked result. Callers own the page around it (header, sidebar, nav).

import React, { useState } from 'react'

// ── Confidence -> visual class mapping ──────────────────────────────────────
// Matches conf-clear/conf-likely/conf-uncertain used throughout AssessmentPage.

const CONFIDENCE_META = {
  high:   { cls: 'conf-clear',     label: 'High confidence' },
  medium: { cls: 'conf-likely',    label: 'Moderate confidence' },
  low:    { cls: 'conf-uncertain', label: 'Lower confidence' },
}

// ── Spanish category -> plain English label ──────────────────────────────────

const CATEGORY_LABELS = {
  rendimiento_trabajo: 'Employment / work income',
  rendimiento_actividad_economica: 'Business / self-employment income',
  rendimiento_capital_mobiliario: 'Savings income (dividends, interest)',
  rendimiento_capital_inmobiliario: 'Rental income',
  ganancia_patrimonial: 'Capital gain',
  imputacion_rentas: 'Imputed property income',
  uncertain: 'Uncertain — needs review',
}

// ── Formatting helpers ────────────────────────────────────────────────────────

export function formatEur(amount) {
  if (amount === null || amount === undefined) return '—'
  return '€' + Math.round(amount).toLocaleString('en')
}

export function formatPct(pct) {
  if (pct === null || pct === undefined) return '—'
  return `${pct}%`
}

// Mirrors getPeriodMultiplier() in supabase/functions/_shared/calculators/
// calculatorHelpers.ts. Duplicated here because that file lives in the Deno
// edge function runtime, not reachable from this CRA frontend bundle — if
// the period rules ever change there, update this too.
function getOriginalPeriodMultiplier(period) {
  switch (period) {
    case 'annual':       return 1
    case 'monthly':       return 12
    case 'one_time':      return 1
    case 'partial_year':  return 1
    default:              return 1
  }
}

// Formats an original-currency amount as a parenthetical, e.g. "($9,130 USD)".
// Returns null when the item was entered in EUR or original data is missing.
function formatOriginalParenthetical(item) {
  if (!item || !item.currency || item.currency.toUpperCase() === 'EUR') return null
  // For rental items, show the net (post-expense, post-depreciation) original amount,
  // not the gross — the EUR figure on the result page is also net.
  const original = item.income_type === 'rental'
    ? item.net_amount_original
    : item.gross_amount_original
  if (original === null || original === undefined) return null

  // Was reading gross_amount_original raw — that's the AS-ENTERED figure in
  // its original period (e.g. a monthly amount), while every EUR figure on
  // this page is already annualized. A $5,000/month entry rendered as
  // "($5,000 USD)" next to an annualized €51,000 looked like a broken
  // exchange rate; it was actually a period mismatch, not an FX bug.
  const annualized = original * getOriginalPeriodMultiplier(item.amount_period)

  const CURRENCY_SYMBOLS = { USD: '$', GBP: '£', CHF: 'CHF ', CAD: 'CA$', MXN: 'MX$' }
  const symbol = CURRENCY_SYMBOLS[item.currency.toUpperCase()] ?? ''
  const formatted = Math.round(annualized).toLocaleString('en')
  return `(${symbol}${formatted} ${item.currency.toUpperCase()})`
}

// ── Category display label ────────────────────────────────────────────────────
// Resolves the human-readable category label for a classified item.
// Pension items (income_type === 'pension') map to rendimiento_trabajo in Spain
// but "Employment income" is confusing to users — override to "Pension income".

function getCategoryLabel(item) {
  if (item.income_type === 'pension' && item.spanish_category === 'rendimiento_trabajo') {
    return 'Pension income'
  }
  return CATEGORY_LABELS[item.spanish_category] || item.spanish_category
}

// Returns a short note describing what expense detail was modeled for rental items.
// Shown as a sub-line under the amount in the classification table.
function getRentalAmountNote(item) {
  if (item.income_type !== 'rental') return null
  const depSource = item.rental_depreciation_source
  const depNote = depSource === 'none' || !depSource
    ? 'depreciation not modeled'
    : 'depreciation included'

  if (item.rental_expense_path === 'fast_path_net') {
    return `Net income entered · ${depNote}`
  }
  if (item.rental_expense_path === 'itemized') {
    return `Expenses itemized · ${depNote}`
  }
  return 'Gross income only — expenses not modeled'
}

// ── Classification table ──────────────────────────────────────────────────────
// Now includes: EUR amount, % of its base, and estimated tax contribution.
// Tax contribution is computed proportionally: an item's share of its base
// (general_tax or savings_tax) times that item's share of the base total.
// general_tax + savings_tax == irpf_gross (see irpfStandalone.ts).

function ClassificationTable({ result }) {
  const { classified_items, general_base, savings_base, general_tax, savings_tax, review_items,
          personal_minimum_used, pension_reduction_used, adjustment_items } = result
  const [expandedIds, setExpandedIds] = useState({})

  function toggleExpand(id) {
    setExpandedIds(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const flagsByLabel = {}
  for (const ri of (review_items || [])) {
    if (!flagsByLabel[ri.label]) flagsByLabel[ri.label] = []
    if (!flagsByLabel[ri.label].find(x => x.flag === ri.flag)) {
      flagsByLabel[ri.label].push(ri)
    }
  }

  // Build lookup helpers for rows that are tied to a planning adjustment.
  // The engine currently passes items_in_scope as subtype keys, but this is
  // intentionally permissive so saved/older results still get marked if they
  // use ids or labels.
  const adjustmentScopeKeys = new Set(
    (adjustment_items || []).flatMap(ai => ai.items_in_scope || []).filter(Boolean)
  )
  const adjustmentLabels = new Set(
    (adjustment_items || []).map(ai => ai.label).filter(Boolean)
  )

  function hasAdjustmentDriver(item) {
    if (!adjustment_items || adjustment_items.length === 0) return false
    return (
      adjustmentScopeKeys.has(item.income_subtype) ||
      adjustmentScopeKeys.has(item.income_type) ||
      adjustmentScopeKeys.has(item.id) ||
      adjustmentLabels.has(item.label)
    )
  }

  // Pre-compute the gross sum of included items per base.
  const grossByBase = { general: 0, savings: 0 }
  for (const ci of classified_items) {
    if (ci.included_in_estimate && ci.net_amount_eur != null) {
      if (grossByBase[ci.spanish_tax_base] !== undefined) {
        grossByBase[ci.spanish_tax_base] += ci.net_amount_eur
      }
    }
  }
  const includedGrossTotal = grossByBase.general + grossByBase.savings

  const totalMinimumDeduction =
    ((personal_minimum_used?.total) || 0) + (pension_reduction_used || 0)

  return (
    <div className="cal-classification-section">
      <div className="cal-section-eyebrow">How Spain likely sees your income</div>
      <p className="cal-section-subtitle">
        Spain classifies your income into General Base or Savings Base, both with very different tax tables. Below is our best guess for how each item will be treated for Spanish tax.
        Click a higlighted item to review notes.
      </p>

      <div className="cal-result-table cal-result-table--v2">
        <div className="cal-result-table-header">
          <span>Income</span>
          <span>Amount</span>
          <span>Spain likely treats as</span>
          <span>Est. tax</span>
          {/* <span>Review</span> */}
        </div>

        {classified_items.map(item => {
          const conf = CONFIDENCE_META[item.classification_confidence] || CONFIDENCE_META.low
          const amount = item.net_amount_eur
          const itemFlags = flagsByLabel[item.label] || []
          const hasFlags = itemFlags.length > 0
          const isExpanded = !!expandedIds[item.id]
          const hasAdjustment = hasAdjustmentDriver(item)

          let baseGrossTotal = null
          let baseTax = null
          if (item.spanish_tax_base === 'general') { baseGrossTotal = grossByBase.general; baseTax = general_tax }
          else if (item.spanish_tax_base === 'savings') { baseGrossTotal = grossByBase.savings; baseTax = savings_tax }

          const canApportion = item.included_in_estimate && amount != null
            && typeof baseGrossTotal === 'number' && baseGrossTotal > 0
            && typeof baseTax === 'number'
          const estTax = canApportion ? (amount / baseGrossTotal) * baseTax : null

          return (
            <React.Fragment key={item.id}>
              <div
                className={`cal-result-table-row${hasFlags ? ' cal-result-table-row--flagged' : ''}${isExpanded ? ' cal-result-table-row--expanded' : ''}${hasFlags ? ' cal-result-table-row--clickable' : ''}${hasAdjustment ? ' cal-result-table-row--adjustment' : ''}`}
                onClick={hasFlags ? () => toggleExpand(item.id) : undefined}
              >
                {/* Col 1: Income label */}
                <span className="cal-result-table-label">
                  {hasFlags && (
                    <span className="cal-result-table-flag-indicator">
                      {isExpanded ? '▾' : '▸'}
                    </span>
                  )}
                  {hasAdjustment && (
                    <span className="cal-result-table-adjustment-dot" title="Could change this estimate">*</span>
                  )}
                  {item.label}
                </span>

                {/* Col 2: Amount */}
                <span className="cal-result-table-amount">
                  {formatEur(amount)}
                  {(() => {
                    const parens = formatOriginalParenthetical(item)
                    return parens ? <span className="cal-result-table-original-amount">{parens}</span> : null
                  })()}
                  {item.income_type === 'rental' && (
                    <span className="cal-result-table-rental-note">
                      {getRentalAmountNote(item)}
                    </span>
                  )}
                </span>

                {/* Col 3: Spain likely treats as (category + base pill) */}
                <span className="cal-result-table-category">
                  {getCategoryLabel(item)}
                  {item.spanish_tax_base === 'general' && (
                    <span className="cal-result-table-base-pill cal-result-table-base-pill--general">General base</span>
                  )}
                  {item.spanish_tax_base === 'savings' && (
                    <span className="cal-result-table-base-pill u-pill-neutral">Savings base</span>
                  )}
                </span>

                {/* Col 4: Est. tax */}
                <span className="cal-result-table-tax">
                  {estTax !== null ? formatEur(estTax) : '—'}
                  {hasAdjustment && (
                    <span className="cal-result-table-scenario-marker" title="See what could change this estimate">*</span>
                  )}
                </span>

                {/* Col 5: Review (confidence badge) */}
                {/* <span className={`cal-result-table-included${item.included_in_estimate ? ' included' : ' excluded'}`}>
                  <span className={`asmt-resp-conf ${conf.cls}`}>
                    <span className="asmt-resp-conf-dot" />
                    <span className="asmt-resp-conf-label">
                      {item.included_in_estimate ? conf.label.replace(' confidence', '') : 'Not included'}
                    </span>
                  </span>
                </span> */}
              </div>

              {hasFlags && isExpanded && (
                <div className="cal-result-table-flags">
                  {itemFlags.map((ri, i) => (
                    <div className="u-flag-row" key={i}>
                      <span className="cal-result-table-flag-bullet">↳</span>
                      <span>{ri.explanation}</span>
                    </div>
                  ))}
                </div>
              )}
            </React.Fragment>
          )
        })}

        {/* Gross total row */}
        <div className="cal-result-table-row cal-result-table-row--total">
          <span className="cal-result-table-label">Gross income total</span>
          <span className="cal-result-table-amount">{formatEur(includedGrossTotal)}</span>
          <span />
          <span className="cal-result-table-tax">
            {(typeof general_tax === 'number' && typeof savings_tax === 'number')
              ? formatEur(general_tax + savings_tax)
              : formatEur(result.irpf_gross)}
          </span>
          <span />
        </div>

        {/* Minimum deduction row */}
        {totalMinimumDeduction > 0 && (
          <div className="cal-result-table-row cal-result-table-row--deduction">
            <span className="cal-result-table-label">
              Personal / family minimum
              {pension_reduction_used > 0 ? ' + pension reduction' : ''}
            </span>
            <span className="cal-result-table-amount cal-result-table-amount--deduction">
              − {formatEur(totalMinimumDeduction)}
            </span>
            <span className="cal-result-table-deduction-note">Applied before tax calculation</span>
            <span />
            <span />
          </div>
        )}
      </div>
    </div>
  )
}

// ── Scenario hero — shown when display_mode === 'classification_scenarios' ────

function ScenarioHero({ result }) {
  const scenarios = result.classification_scenarios
  if (!scenarios) return null

  return (
    <div className="cal-scenario-hero">
      <div className="cal-scenario-driver-note">
        <span className="cal-scenario-driver-note-label">Classification-sensitive estimate</span>
        {result.scenario_driver_note}
      </div>

      <div className="cal-scenario-cards">
        {scenarios.map(s => (
          <div
            key={s.key}
            className={`cal-scenario-card${s.is_default ? ' cal-scenario-card--default' : ''}`}
          >
            {s.is_default && (
              <div className="cal-scenario-card-badge">Default planning view</div>
            )}
            <div className="cal-scenario-card-label">{s.label}</div>
            <div className="cal-scenario-card-amount">
              <span className="cal-scenario-card-approx"></span>
              {formatEur(Math.round(s.irpf_gross / 100) * 100)}
            </div>
            <div className="cal-scenario-card-rate">
              {s.effective_rate_on_total_income !== null
                ? `${s.effective_rate_on_total_income}% effective rate`
                : '—'}
            </div>
            <div className="cal-scenario-card-desc">{s.description}</div>
          </div>
        ))}
      </div>

      <p className="cal-scenario-not-elections">
        These are not elections — they are planning views based on different possible classifications.
        Your advisor should determine which treatment fits the entity facts and documentation.
      </p>

      {result.scenario_advisor_question && (
        <div className="cal-scenario-advisor-q">
          <span className="cal-scenario-advisor-q-label">Question for your advisor</span>
          {result.scenario_advisor_question}
        </div>
      )}
    </div>
  )
}

// ── Standard hero — always shows a single point estimate ─────────────────────
// Range mode is retired. All results show irpf_gross as the planning estimate.
// When adjustment_items are present, AdjustmentItemsSection renders below this.

function StandardHero({ result, filingComparison = null }) {
  const isAdjustment = result.display_mode === 'adjustment_estimate'
  const isIndividualRecommended = filingComparison && filingComparison.recommended_mode === 'individual_v1'

  return (
    <div className="asmt-irpf-card cal-hero-card">
      <div className="asmt-irpf-metric-row">
        <div className="asmt-irpf-metric asmt-irpf-metric--hero">
          <div className="cal-hero-eyebrow">
            {isAdjustment ? 'Planning estimate' : isIndividualRecommended ? 'Your individual return' : 'Estimated IRPF'}
          </div>
          <div className="asmt-irpf-metric-value--hero">
            <span className="cal-approx-symbol"></span>
            {formatEur(Math.round(result.irpf_gross / 100) * 100)}
          </div>
          {isIndividualRecommended && (
            <div className="cal-hero-baseline-note">
              This is your own return only — not your household total. See the comparison
              below for what you and your spouse owe combined.
            </div>
          )}
          {isAdjustment && (
            <div className="cal-hero-baseline-note">
              Conservative baseline. All uncertain items included as taxable.
            </div>
          )}
          {isAdjustment && (
            <div className="cal-hero-info-badge">
              <span className="cal-hero-info-icon">ⓘ</span>
              This is not your final tax. See what could change this estimate below.
            </div>
          )}
        </div>
        <div className="asmt-irpf-metric cal-hero-rate-col">
          <div className="cal-hero-eyebrow cal-hero-eyebrow--right">Effective rate</div>
          <div className="asmt-irpf-metric-value cal-hero-rate-value">
            {formatPct(result.effective_rate_on_total_income)}
          </div>
          <div className="cal-hero-rate-pill">Based on total income</div>
        </div>
      </div>
    </div>
  )
}

function SpouseClassificationSection({ spouseResult }) {
  const [expanded, setExpanded] = useState(false)
  if (!spouseResult?.classified_items?.length) return null

  return (
    <div className="cal-spouse-classification">
      <div className="cal-spouse-classification-label">Spouse income used for filing comparison</div>
      <p className="u-hint">
        We also included your spouse's income when comparing individual vs. joint filing. This is
        their own individual return, shown for reference — it isn't part of your return above, and
        the two totals shouldn't be added together as if they were one filing.
      </p>
      <button className="asmt-irpf-detail-toggle" onClick={() => setExpanded(e => !e)}>
        {expanded ? '▾' : '▸'} View your spouse's income breakdown
      </button>
      {expanded && <ClassificationTable result={spouseResult} />}
    </div>
  )
}

// ── Filing comparison — individual vs. joint household result ────────────────
// Rendered when filing_comparison came back from the edge function (married
// user; see filingComparison.ts). The hero above already shows whichever
// scenario the engine recommended (result was overridden server-side in
// index.ts) — this card is what actually explains the recommendation and
// shows both household totals side by side, since the hero alone can't
// convey "here's the option we didn't pick and by how much."
function FilingComparisonCard({ filingComparison }) {
  if (!filingComparison) return null

  const isJointRecommended = filingComparison.recommended_mode === 'joint_orientation_v1'

  return (
    <div className="cal-filing-comparison">
      <div className="cal-filing-comparison-label">Individual vs. joint filing</div>
      <p className="cal-filing-comparison-recommendation">
        We recommend filing <strong>{isJointRecommended ? 'jointly' : 'individually'}</strong> —
        it saves your household {formatEur(filingComparison.savings_vs_alternative)} compared to
        the alternative.
      </p>

      <div className="cal-filing-comparison-grid">
        <div className={`cal-filing-comparison-option${!isJointRecommended ? ' cal-filing-comparison-option--selected' : ''}`}>
          <div className="u-label-sm">Filing individually</div>
          <div className="cal-filing-comparison-amount">{formatEur(filingComparison.household_tax_individual)}</div>
          <div className="u-hint">
            Your return ({formatEur(filingComparison.self_result.irpf_gross)}) + your spouse's
            return ({formatEur(filingComparison.spouse_result.irpf_gross)})
          </div>
        </div>
        <div className={`cal-filing-comparison-option${isJointRecommended ? ' cal-filing-comparison-option--selected' : ''}`}>
          <div className="u-label-sm">Filing jointly</div>
          <div className="cal-filing-comparison-amount">{formatEur(filingComparison.household_tax_joint)}</div>
          <div className="u-hint">One combined return for both of you</div>
        </div>
      </div>

      {filingComparison.assumptions?.length > 0 && (
        <div className="u-stack-xs cal-filing-comparison-assumptions">
          {filingComparison.assumptions.map((a, i) => (
            <div className="u-item-note" key={i}>{a}</div>
          ))}
        </div>
      )}
    </div>
  )
}


// Shown when display_mode === 'adjustment_estimate'.
// Each item is an independent assumption that could reduce the baseline.
// Items are pre-sorted by potential_reduction descending (engine guarantees this).

function AdjustmentItemsSection({ result }) {
  const items = result.adjustment_items
  // Cards default to expanded (advisor note visible). User can collapse individually.
  const [collapsed, setCollapsed] = useState({})
  if (!items || items.length === 0) return null

  function toggle(key) {
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }))
  }

  return (
    <div className="cal-adjustment-section">
      <div className="cal-section-eyebrow-dark">What could change this estimate</div>
      <p className="cal-section-subtitle">
        Rows marked with * above are tied to these assumptions. Each can apply or not.
        Confirm each with a professional before filing.
      </p>
      <div className="cal-adjustment-items">
        {items.map(item => {
          const isCollapsed = !!collapsed[item.key]
          return (
            <div className="cal-adjustment-item" key={item.key}>
              {/* Card header — clickable to collapse */}
              <div className="cal-adjustment-item-header" onClick={() => toggle(item.key)}>
                <span className="cal-adjustment-item-title-wrap">
                  <span className="cal-adjustment-item-marker">*</span>
                  <span className="cal-adjustment-item-label">{item.label}</span>
                </span>
                <span className="cal-adjustment-item-chevron">{isCollapsed ? '∧' : '∨'}</span>
              </div>

              {/* Condition */}
              <div className="cal-adjustment-item-condition">{item.condition}</div>

              {/* Reduction amount */}
              <div className="cal-adjustment-item-reduction-label">Potential reduction</div>
              <div className="cal-adjustment-item-reduction">
                −{formatEur(item.potential_reduction)}
              </div>

              {/* New estimate */}
              <div className="cal-adjustment-item-result">
                New estimate if this applies:{' '}
                <span className="cal-adjustment-item-result-amount">
                  ≈{formatEur(Math.round(item.scenario_irpf / 100) * 100)}
                </span>
              </div>

              {/* Advisor note — shown by default, hidden when collapsed */}
              {!isCollapsed && item.advisor_note && (
                <>
                  <div className="cal-adjustment-item-divider" />
                  <div className="cal-adjustment-item-advisor-note">{item.advisor_note}</div>
                </>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Hero through disclaimer, in the order the Calculator result page has always drawn them.
export function IrpfEstimateBody({ result, filingComparison = null, onEditFxAssumptions = null }) {
  const [showDetail, setShowDetail] = useState(false)

  const excludedItems = result.classified_items.filter(i => !i.included_in_estimate)
  const includedCount = result.classified_items.length - excludedItems.length

  return (
    <>
      {/* ── 1. Hero — display_mode determines which component renders ── */}
      {/* classification_scenarios: legacy mode, only in old saved results */}
      {result.display_mode === 'classification_scenarios' && result.classification_scenarios
        ? <ScenarioHero result={result} />
        : <StandardHero result={result} filingComparison={filingComparison} />
      }

      <FilingComparisonCard filingComparison={filingComparison} />

      {/* ── 2. Classification table ───────────────────────────────────── */}
      <ClassificationTable result={result} />
      {excludedItems.length > 0 && (
        <p className="cal-result-excluded-note">
          {excludedItems.length} item{excludedItems.length !== 1 ? 's' : ''} not included in the
          numeric estimate — treatment is uncertain. See review notes above.
        </p>
      )}

      {/* Spouse's own return, shown separately — never merged into the
          table above. Only relevant when individual filing was
          recommended; under joint, the table above already IS the
          combined household return. */}
      {filingComparison?.recommended_mode === 'individual_v1' && (
        <SpouseClassificationSection spouseResult={filingComparison.spouse_result} />
      )}

      {/* ── 3. Adjustment items — shown when display_mode === 'adjustment_estimate' ── */}
      <AdjustmentItemsSection result={result} />

      {/* ── 4. Base breakdown ────────────────────────────────────────── */}
      {/* <div className="asmt-irpf-drivers cal-base-breakdown-section">
        <div className="cal-section-eyebrow-teal">What's in each tax base</div>
        <BaseBreakdownTable result={result} />
      </div> */}

      {/* ── Filing context note ─────────────────────────────────────────── */}
      {/* Was reading result.joint_filing_note, a field the engine stopped
          returning when filing_context replaced it with a structured
          object — this had been silently dead ever since, so married
          users never saw a filing-mode explanation here at all. */}
      {result.filing_context?.note && (
        <div className="asmt-irpf-missing">
          <div className="asmt-irpf-missing-label">About your filing estimate</div>
          <div className="asmt-irpf-missing-item">{result.filing_context.note}</div>
        </div>
      )}

      {/* ── Foreign tax context ───────────────────────────────────────── */}
      {result.possible_foreign_tax_relief && (
        <div className="asmt-irpf-missing">
          <div className="asmt-irpf-missing-label">Foreign tax paid</div>
          <div className="asmt-irpf-missing-item">
            You reported {formatEur(result.total_foreign_tax_paid)} in foreign tax paid on income
            included in this estimate. This is shown for context only — Spain may allow you to
            offset some Spanish tax for foreign tax paid on the same income, but that calculation
            requires professional review and is not included in this estimate.
          </div>
        </div>
      )}

      {/* ── Warnings ───────────────────────────────────────────────────── */}
      {result.warnings.length > 0 && (
        <div className="asmt-irpf-warnings">
          {result.warnings.map((w, i) => (
            <div className="asmt-irpf-warning" key={i}>{w}</div>
          ))}
        </div>
      )}

      {/* ── 6. Assumptions / methodology — collapsible ───────────────── */}
      <button className="asmt-irpf-detail-toggle" onClick={() => setShowDetail(s => !s)}>
        {showDetail ? '▾' : '▸'} View inputs, assumptions &amp; methodology
      </button>

      {showDetail && (
        <div className="asmt-irpf-detail">
          <div className="u-stack-xs">
            <div className="asmt-irpf-detail-label">Inputs used</div>
            <div className="asmt-irpf-inputs-table">
              {result.inputs_used.map((input, i) => (
                <div className="asmt-irpf-input-row" key={i}>
                  <span className="asmt-irpf-input-label">{input.label}</span>
                  <span className="asmt-irpf-input-value">{input.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="u-stack-xs">
            <div className="asmt-irpf-detail-label">Assumptions</div>
            {result.assumptions.map((a, i) => (
              <div className="u-item-note" key={i}>{a}</div>
            ))}
            {result.fx_rates_used && Object.keys(result.fx_rates_used).length > 0 && (
              <div className="cal-fx-disclosure">
                {Object.entries(result.fx_rates_used).map(([currency, entry]) => (
                  <span key={currency} className="cal-fx-disclosure-rate">
                    {currency} converted at {entry.rate_to_eur}
                    {entry.source === 'user_override'
                      ? ` (your rate — HolaSam default: ${entry.default_rate})`
                      : ' (HolaSam planning rate)'}
                    {' '}
                  </span>
                ))}
                {onEditFxAssumptions && (
                  <button className="cal-fx-edit-link" onClick={onEditFxAssumptions}>
                    Edit assumptions
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="u-stack-xs">
            <div className="asmt-irpf-detail-label">Rules used</div>
            <div className="u-item-note">
              Requested tax year: {result.rules_used.requested_tax_year} · IRPF brackets: {result.rules_used.irpf_rules_year} · Personal minimums: {result.rules_used.minimums_rules_year}
            </div>
          </div>
        </div>
      )}

      <p className="triage-disclaimer">
        This is an orientation estimate, not a tax filing calculation. It does not replace
        professional advice. {includedCount} of {result.classified_items.length} income item
        {result.classified_items.length !== 1 ? 's are' : ' is'} reflected in the numeric estimate above.
      </p>
    </>
  )
}
